import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Gender } from "../models/gender";
import { IndicatorCode } from "../models/indicators/indicator-code";
import { Observable, of } from "rxjs";
import { QuestionType } from "../models/survey";
import { shareReplay } from "rxjs/operators";
import { Lookup, NumericLookup } from "./../models/lookup";
import { IndicatorType } from "../models/indicator-types";
import { AdministrativeUnit } from "../models/organizations/administrative-unit";
import { School } from "../models/organizations/school";
import { SppIndicatorType } from "../models/indicators/spp/spp-indicator";
import { WorkflowStatus, WorkflowStatusChange } from "../models/workflow";
import { RoleListItem, TargetUserType } from "../models/user-management/role";
import { DisputeCaseType } from "../models/dispute-resolution/dispute-base-models";
@Injectable({
  providedIn: "root",
})
export class LookupService {
  private baseUrl = "api/lookup";

  I14OutcomesTypes$: Observable<Lookup[]>;
  I14OutcomesTypes: Lookup[];

  I14OutcomesTypeGroups$: Observable<Lookup[]>;
  I14OutcomesTypeGroups: Lookup[];

  I14OutcomesTypeSubGroups$: Observable<Lookup[]>;
  I14OutcomesTypeSubGroups: Lookup[];

  questionTypeData$: Observable<QuestionType[]>;
  questionTypes: QuestionType[];

  cdeRolesData$: Observable<RoleListItem[]>;
  cdeRoles: RoleListItem[];

  auRolesData$: Observable<RoleListItem[]>;
  auRoles: RoleListItem[];

  administrativeUnitData$: Observable<AdministrativeUnit[]>;
  administrativeUnits: AdministrativeUnit[];

  schoolYears$: Observable<Lookup[]>;
  schoolYears: Lookup[];

  capProgramAreas$: Observable<Lookup[]>;
  capProgramAreas: Lookup[];

  constructor(private http: HttpClient) {}

  getQuestionTypes() {
    if (!this.questionTypeData$) {
      this.questionTypeData$ = this.http
        .get<QuestionType[]>(`${this.baseUrl}/question-types`)
        .pipe(shareReplay(1));
      this.questionTypeData$.subscribe((data) => (this.questionTypes = data));
    }
    return this.questionTypeData$;
  }

  getCdeRoles() {
    if (!this.cdeRolesData$) {
      this.cdeRolesData$ = this.http
        .get<RoleListItem[]>(`${this.baseUrl}/cde-roles`) //{Name, Description}
        .pipe(shareReplay(1));
      this.cdeRolesData$.subscribe((data) => (this.cdeRoles = data));
    }
    return this.cdeRolesData$;
  }

  getAuRoles() {
    if (!this.auRolesData$) {
      this.auRolesData$ = this.http
        .get<RoleListItem[]>(`${this.baseUrl}/au-roles`) //{Name, Description}
        .pipe(shareReplay(1));
      this.auRolesData$.subscribe((data) => (this.auRoles = data));
    }
    return this.auRolesData$;
  }

  getRolesByGroup(group: string, type: TargetUserType) {
    return this.http.get<any[]>(
      `${this.baseUrl}/getrolesbygroup/${group}/${type}`
    ); //{Name, Description}
  }

  getGenders() {
    return this.http.get<Gender[]>(`${this.baseUrl}/gender`);
  }

  getRaces() {
    return this.http.get<Lookup[]>(`${this.baseUrl}/races`);
  }

  getDisabilities() {
    return this.http.get<Lookup[]>(`${this.baseUrl}/disabilities`);
  }

  getSchools(administrativeUnitId: string) {
    if (administrativeUnitId) {
      return this.http.get<School[]>(
        `${this.baseUrl}/schools/${administrativeUnitId}`
      );
    }
    return this.http.get<School[]>(`${this.baseUrl}/schools`);
  }

  getShortSchools(administrativeUnitId: string) {
    if (administrativeUnitId) {
      return this.http.get<Lookup[]>(
        `${this.baseUrl}/schools-short/${administrativeUnitId}`
      );
    }
    return this.http.get<Lookup[]>(`${this.baseUrl}/schools-short`);
  }

  getIndicatorCodes(indicatorType: IndicatorType, schoolYear: string) {
    return this.http.get<IndicatorCode[]>(
      `${this.baseUrl}/${indicatorType}/codes/${schoolYear}`
    );
  }

  getSppIndicatorTypes() {
    return this.http.get<SppIndicatorType[]>(
      `${this.baseUrl}/spp-indicator-types`
    );
  }

  getSchoolYears() {
    if (!this.schoolYears$) {
      this.schoolYears$ = this.http
        .get<Lookup[]>(`${this.baseUrl}/school-years`)
        .pipe(shareReplay(1));
      this.schoolYears$.subscribe((data) => (this.schoolYears = data));
    }
    return this.schoolYears$;
  }

  getDocumentTypes() {
    return this.http.get<Lookup[]>(`${this.baseUrl}/document-types`);
  }

  getAuAdministrativeUnits() {
    if (!this.administrativeUnitData$) {
      this.administrativeUnitData$ = this.http
        .get<AdministrativeUnit[]>(`${this.baseUrl}/au-tenants`)
        .pipe(shareReplay(1));
      this.administrativeUnitData$.subscribe(
        (data) => (this.administrativeUnits = data)
      );
    }
    return this.administrativeUnitData$;
  }

  getI14OutcomesTypes() {
    if (!this.I14OutcomesTypes$) {
      this.I14OutcomesTypes$ = this.http
        .get<Lookup[]>(`${this.baseUrl}/indicator14-outcome-types`)
        .pipe(shareReplay(1));
      this.I14OutcomesTypes$.subscribe(
        (data) => (this.I14OutcomesTypes = data)
      );
    }
    return this.I14OutcomesTypes$;
  }

  getI14OutcomesTypeGroups() {
    if (!this.I14OutcomesTypeGroups$) {
      this.I14OutcomesTypeGroups$ = this.http
        .get<Lookup[]>(`${this.baseUrl}/indicator14-outcome-groups`)
        .pipe(shareReplay(1));
      this.I14OutcomesTypeGroups$.subscribe(
        (data) => (this.I14OutcomesTypeGroups = data)
      );
    }
    return this.I14OutcomesTypeGroups$;
  }

  getI14OutcomesTypeSubGroups() {
    if (!this.I14OutcomesTypeSubGroups$) {
      this.I14OutcomesTypeSubGroups$ = this.http
        .get<Lookup[]>(`${this.baseUrl}/indicator14-outcome-subgroups`)
        .pipe(shareReplay(1));
      this.I14OutcomesTypeSubGroups$.subscribe(
        (data) => (this.I14OutcomesTypeSubGroups = data)
      );
    }
    return this.I14OutcomesTypeSubGroups$;
  }

  getCapProgramAreas(schoolYear: string) {
    return this.http
      .get<Lookup[]>(`${this.baseUrl}/cap-program-areas/${schoolYear}`)
      .pipe(shareReplay(1));
  }

  getSppIndicatorColors() {
    return of([
      { id: "black", label: "Black" },
      { id: "blue", label: "Blue" },
      { id: "green", label: "Green" },
      { id: "red", label: "Red" },
      { id: "yellow", label: "Yellow" },
    ]);
  }

  getCapAllWorkflowStatuses() {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/cap-all-workflow-statuses`
    );
  }

  getCapDraftWorkflowStatusChanges() {
    return this.http.get<WorkflowStatusChange[]>(
      `${this.baseUrl}/cap-draft-workflow-status-changes`
    );
  }

  getDRWorkflowStatusChangesByCaseType(
    disputeCaseType: DisputeCaseType,
    currentStatusId: string
  ) {
    return this.http.get<WorkflowStatusChange[]>(
      `${this.baseUrl}/dr-workflow-status-changes/${disputeCaseType}/${currentStatusId}`
    );
  }

  getCapActionAllWorkflowStatuses() {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/cap-action/all-workflow-statuses`
    );
  }

  getCapActionNotStartedWorkflowStatusChanges() {
    return this.http.get<WorkflowStatusChange[]>(
      `${this.baseUrl}/cap-action/not-started-workflow-status-changes`
    );
  }

  getGiftedFiscalAllWorkflowStatuses() {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/gifted-fiscal-all-workflow-statuses`
    );
  }

  getCurrentWorkFlowStatusChangesForAdministrativeUnitSurvey(
    currentWorkflowStatusId: string,
    workflowId: string,
    lang: string,
    administrativeUnitSurveyId: string,
    hideSpinner: boolean = false
  ) {
    const headerDict = {
      "no-spinner": "stops spinner from showing",
    };

    const requestOptions = {
      headers: hideSpinner ? new HttpHeaders(headerDict) : new HttpHeaders(),
    };

    var request = {
      currentWorkflowStatusId: currentWorkflowStatusId,
      workflowId: workflowId,
      lang: lang,
      administrativeUnitSurveyId: administrativeUnitSurveyId,
    };

    return this.http.post<WorkflowStatusChange[]>(
      `${this.baseUrl}/workflow/get-administrativeUnitsurvey-workflowstatuses`,
      request,
      requestOptions
    );
  }

  getSystemLookupTypes(): Observable<NumericLookup[]> {
    return this.http.get<NumericLookup[]>(
      `${this.baseUrl}/system-upload-types`
    );
  }

  getPersonnelStatusAuWorkflowStatuses(): Observable<WorkflowStatus[]> {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/personnel-status/all-au-workflow-statuses`
    );
  }

  getPersonnelStatusRecordWorkflowStatuses(): Observable<WorkflowStatus[]> {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/personnel-status/all-record-workflow-statuses`
    );
  }
}
